import React from 'react'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { Scores } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

type SubscaleResultsConfig = {
  title: string
  totalKey: keyof Scores
  testIdSlug: string
  subscores: { key: keyof Scores; label: string }[]
}

type SubscaleResultsEntryProps = SubscaleResultsConfig & {
  describeScore: (assessmentName: string, score: number) => string
  onViewAnswersClick: (assessmentName: string) => void
  scores: Scores
  isLastItem?: boolean
}

const TableRow = styled.View<{ theme: ThemeType; alignTop?: boolean; isLastItem?: boolean }>(
  ({ theme: { colors, spacing }, alignTop, isLastItem }) => ({
    flexDirection: 'row',
    alignItems: alignTop ? 'flex-start' : 'center',
    padding: spacing['24px'],
    borderBottomWidth: isLastItem ? '0' : '1px',
    borderBottomColor: isLastItem ? undefined : colors.borderDefault,
  }),
)

const AssessmentContentContainer = styled.View({
  flexDirection: 'row',
  flexBasis: '320px',
  alignItems: 'center',
})

const ViewAnswersButton = styled(TextButton)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['8px'],
  marginTop: theme.spacing['6px'],
}))

const ScoreContentContainer = styled.View({
  flexDirection: 'row',
  flexBasis: '80px',
})

const QuestionContainer = styled.View<{ theme: ThemeType; columnLayout?: boolean }>(
  ({ theme: { spacing }, columnLayout }) => ({
    flexDirection: columnLayout ? 'column' : 'row',
    alignItems: columnLayout ? 'flex-start' : 'center',
    marginTop: spacing['8px'],
  }),
)

export const SUBSCALE_RESULTS_CONFIGS: SubscaleResultsConfig[] = [
  {
    title: 'BEST',
    totalKey: 'BEST-TOTAL',
    testIdSlug: 'best',
    subscores: [
      { key: 'BEST-NEGATIVE-BEHAVIORS', label: 'Negative Behaviors' },
      { key: 'BEST-POSITIVE-BEHAVIORS', label: 'Positive Behaviors' },
      { key: 'BEST-THOUGHTS-FEELINGS', label: 'Thoughts & Feelings' },
    ],
  },
  {
    title: 'DERS',
    totalKey: 'DERS-TOTAL',
    testIdSlug: 'ders',
    subscores: [
      { key: 'DERS-AWARENESS', label: 'Awareness' },
      { key: 'DERS-CLARITY', label: 'Clarity' },
      { key: 'DERS-GOALS', label: 'Goals' },
      { key: 'DERS-IMPULSE', label: 'Impulse' },
      { key: 'DERS-NONACCEPTANCE', label: 'Nonacceptance' },
      { key: 'DERS-STRATEGIES', label: 'Strategies' },
    ],
  },
  {
    title: 'BPFS-C',
    totalKey: 'BPFSC-TOTAL',
    testIdSlug: 'bpfsc',
    subscores: [
      { key: 'BPFSC-NEGATIVE-RELATIONSHIPS', label: 'Negative Relationships' },
      { key: 'BPFSC-AFFECTIVE-INSTABILITY', label: 'Affective Instability' },
      { key: 'BPFSC-IDENTITY-PROBLEMS', label: 'Identity Problems' },
      { key: 'BPFSC-SELF-HARM', label: 'Self Harm' },
    ],
  },
  {
    title: 'BPFS-P',
    totalKey: 'BPFSP-TOTAL',
    testIdSlug: 'bpfsp',
    subscores: [
      { key: 'BPFSP-NEGATIVE-RELATIONSHIPS', label: 'Negative Relationships' },
      { key: 'BPFSP-AFFECTIVE-INSTABILITY', label: 'Affective Instability' },
      { key: 'BPFSP-IDENTITY-PROBLEMS', label: 'Identity Problems' },
      { key: 'BPFSP-SELF-HARM', label: 'Self Harm' },
    ],
  },
]

export const SubscaleResultsEntry: React.FC<SubscaleResultsEntryProps> = ({
  title,
  totalKey,
  testIdSlug,
  subscores,
  scores,
  onViewAnswersClick,
  describeScore,
  isLastItem,
}) => {
  const theme = useTheme()

  const totalScore = scores[totalKey] as number | null | undefined
  if (totalScore === undefined || totalScore === null) return null

  return (
    <TableRow testID={tID(`ClientOutcomes-results-${testIdSlug}`)} alignTop isLastItem={isLastItem}>
      <AssessmentContentContainer>
        <Title text={title} size={TitleSize.MEDIUM} />
        <ViewAnswersButton
          testID={tID(`ClientOutcomes-viewAnswers-${testIdSlug}`)}
          onPress={() => {
            onViewAnswersClick(totalKey)
          }}
          text='view answers'
        />
      </AssessmentContentContainer>
      <ScoreContentContainer>
        <Title testID={tID(`client-${testIdSlug}-score`)} text={totalScore} size={TitleSize.MEDIUM} />
      </ScoreContentContainer>
      <View>
        <Title
          testID={tID(`client-${testIdSlug}-score-description`)}
          text={describeScore(totalKey, totalScore || 0)}
          size={TitleSize.MEDIUM}
        />
        {subscores.map(({ key, label }) => {
          const value = scores[key] as number | null | undefined
          if (value === undefined || value === null) return null
          return (
            <QuestionContainer key={key} columnLayout>
              <Title text={label} size={TitleSize.XSMALL} />
              <BodyText text={`${value}`} size={BodyTextSize.DEFAULT} color={theme.colors.textSecondary} />
            </QuestionContainer>
          )
        })}
      </View>
    </TableRow>
  )
}
